/**
 * Newsletter Modal
 * Displays a newsletter sign-up popup after a configurable delay.
 * Respects a cookie so it is only shown once per session expiry period.
 * Always opens if the form was just submitted (success/error state).
 *
 * @element newsletter-modal
 * @extends ModalElement
 */
if (!customElements.get('newsletter-modal')) {
  customElements.define(
    'newsletter-modal',
    class NewsletterModal extends ModalElement {
      static #COOKIE_NAME = 'novaworks:newsletter-popup';

      #initialized = false;

      get #testMode() {
        return this.getAttribute('data-test-mode') === 'true';
      }

      get #delay() {
        return this.hasAttribute('data-delay') ? parseInt(this.getAttribute('data-delay')) : 5;
      }

      get #expiry() {
        return this.hasAttribute('data-expiry') ? parseInt(this.getAttribute('data-expiry')) : 30;
      }

      /** True when the form was already submitted (success or error alert present). */
      get #submitted() {
        return this.querySelector('.alert') !== null;
      }

      get shouldLock() { return true; }

      constructor() {
        super();

        // Skip on Shopify's robot-challenge page or when cookies are disabled.
        if (window.location.pathname === '/challenge' || !theme.cookiesEnabled) return;

        if (!theme.config.isTouch || Shopify.designMode) {
          this.#init();
        } else {
          new theme.initWhenVisible(theme.utils.throttle(() => this.#init()));
        }
      }

      #init() {
        if (this.#initialized) return;
        this.#initialized = true;

        // Re-open immediately when the form was just submitted.
        if (this.#submitted) {
          this.#scheduleShow(1);
          return;
        }

        if (this.#testMode || !this.#getCookie(NewsletterModal.#COOKIE_NAME)) {
          this.#scheduleShow(this.#delay);
        }
      }

      #scheduleShow(delaySecs) {
        if (Shopify.designMode) return;
        setTimeout(() => this.show(), delaySecs * 1000);
      }

      afterShow() {
        super.afterShow();
        this.classList.add('show-image');
      }

      afterHide() {
        super.afterHide();
        this.classList.remove('show-image');

        if (this.#testMode) {
          this.#removeCookie(NewsletterModal.#COOKIE_NAME);
          return;
        }
        this.#setCookie(NewsletterModal.#COOKIE_NAME, this.#expiry);
      }

      /** @param {string} name */
      #getCookie(name) {
        return document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`)?.[2] ?? null;
      }

      /**
       * @param {string} name
       * @param {number} days
       */
      #setCookie(name, days) {
        document.cookie = `${name}=true; max-age=${days * 24 * 60 * 60}; path=/`;
      }

      /** @param {string} name */
      #removeCookie(name) {
        document.cookie = `${name}=; max-age=0`;
      }
    }
  );
}
